import * as Sentry from '@sentry/react';
import { Query } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { IDefaultError } from '@src/interfaces/tanstack.interfaces';

import { sanitizeSensitiveData } from './sentry.helpers';

interface ICaptureTanstackErrorParams {
  type: 'query' | 'mutation';
  error: Error;
  queryKey?: readonly unknown[];
  mutationKey?: readonly unknown[];
  variables?: unknown;
  onMutateResult?: unknown;
}

export const captureTanstackError = ({
  type,
  error,
  queryKey,
  mutationKey,
  variables,
  onMutateResult,
}: ICaptureTanstackErrorParams): void => {
  const axiosError = isAxiosError(error);

  Sentry.captureException(error, (scope: Sentry.Scope) => {
    scope.setLevel('error');

    scope.setTag('source', 'tanstack-query');
    scope.setTag('operation.type', type);
    scope.setTag('error.type', axiosError ? 'axios' : 'tanstack');

    scope.setContext('tanstack', {
      type,
      queryKey: sanitizeSensitiveData(queryKey),
      mutationKey: sanitizeSensitiveData(mutationKey),
    });

    scope.setContext('page', {
      href: window.location.href,
    });

    if (variables !== undefined) {
      scope.setContext('variables', {
        value: sanitizeSensitiveData(variables),
      });
    }

    if (onMutateResult !== undefined) {
      scope.setContext('onMutateResult', {
        value: sanitizeSensitiveData(onMutateResult),
      });
    }

    if (axiosError) {
      const status = error.response?.status;

      if (status) {
        scope.setTag('http.status_code', String(status));
      }

      scope.setContext('http', {
        method: error.config?.method?.toUpperCase(),
        url: error.config?.url,
        baseUrl: error.config?.baseURL,
        status,
        statusText: error.response?.statusText,
        requestBody: sanitizeSensitiveData(error.config?.data),
        responseBody: sanitizeSensitiveData(error.response?.data),
      });
    }

    return scope;
  });
};

export const onQueryErrorHandler =
  (callback?: (error: IDefaultError) => void) =>
  (error: IDefaultError, query: Query<unknown, unknown, unknown>): void => {
    captureTanstackError({
      type: 'query',
      error,
      queryKey: query.queryKey,
    });

    callback?.(error);
  };
